import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { LoadingButton } from "@mui/lab";
import { Box, Button, DialogActions, DialogContent, DialogTitle, Divider, Fade, Stack, Typography } from "@mui/material";
import Dialog from '@mui/material/Dialog';
import { makeStyles } from "@mui/styles";
import classNames from "classnames";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Dashboard, Workspace } from "../../../types/files";
import { sendRequestIntroEmailFunc } from '../../../lib/helper';
import { AuthContext } from '../../../contexts/AuthContext';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import useBackwardsCompatible from '../../../hooks/useBackwardsCompatible';

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            minWidth: 500,
            width: 'auto',
            height: 'fit-content',
            padding: 20,
            borderRadius: 16,
        },
    },
    successDialog: {
        '& .MuiDialog-paper': {
            minWidth: 'unset',
            width: 'fit-content',
        },
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.5rem',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 0',
        height: '100%',
        overflowY: 'auto',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    cancelButton: {
        width: 100,
        borderRadius: 40,
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['600'],
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
    saveButton: {
        width: 100,
        borderRadius: 20,
        background: theme.palette.primary.main,
        color: 'white',
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
    title: {
        fontFamily: 'Inter',
        fontWeight: 'bold',
        fontSize: '1.2rem',
        color: '#000',
    },
    headerIcon: {
        color: theme.colors.neutral['500'],
        width: 32,
        height: 32,
    },
    checkIcon: {
        color: theme.colors.primary['500'],
        width: 50,
        height: 50,
        transform: 'translateX(-5px)'
    },
    notice: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: '500',
        color: theme.colors.neutral['600'],
    },
    actor: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: '#666666',
    },
    label: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    sublabel: {
        fontWeight: '500',
        color: theme.colors.neutral['500'],
    },
}));

const IntroductionRequestModal: React.FC<{
    isOpen: boolean,
    workspace?: Workspace,
    dashboard?: Dashboard,
    onClose: () => void,
}> = ({ isOpen, workspace, dashboard, onClose }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const [success, setSuccess] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const { dashboardSource } = useBackwardsCompatible();

    const source = useMemo(() => dashboardSource(dashboard), [dashboard, dashboardSource]);

    const handleSend = useCallback(() => {
        setLoading(true);
        sendRequestIntroEmailFunc({
            workspaceId: workspace?.id!,
            dashboardId: dashboard?.id!,
            requestorEmail: user?.attributes.email,
        }).then(() => {
            setLoading(false);
            setSuccess(true);
        });
    }, [dashboard, user, workspace]);

    useEffect(() => {
        if (success) {
            const timeOut = setTimeout(() => {
                clearTimeout(timeOut);
                onClose();
            }, 4000);

            return () => {
                clearTimeout(timeOut);
                setSuccess(false);
            }
        }
    // eslint-disable-next-line
    }, [success]);

    return (<>
        <Dialog className={classNames(classes.dialog, success && classes.successDialog)} open={isOpen} onClose={onClose}>
            {success ? (
                <DialogContent className={classes.dialogContent}>
                    <Stack direction="column" spacing={2} alignItems="center" width="100%" p="20px 100px">
                        <Fade in={success} timeout={1000}>
                            <CheckCircleIcon className={classes.checkIcon} />
                        </Fade>
                        <Box />
                        <Typography className={classes.title} textAlign="center">
                            {'Introduction request'}<br />
                            {'successfully sent.'}
                        </Typography>
                    </Stack>
                </DialogContent>
            ) : (<>
                <DialogTitle className={classes.dialogTitle}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                        <ForwardToInboxOutlinedIcon className={classes.headerIcon} />
                        <span>{'Introduction request'}</span>
                    </Stack>
                </DialogTitle>
                <Divider sx={{ margin: '8px 0'}} />
                <DialogContent className={classes.dialogContent}>
                    <Typography className={classes.notice}>
                        {'You are about to send an introduction request.'}<br />
                        {'You will be copied on the outgoing email.'}
                    </Typography>
                    <Stack spacing={2} alignItems="flex-start" justifyContent="flex-start" width="100%" my={2}>
                        <Stack direction="row" spacing="12px" alignItems="flex-start" justifyContent="flex-start" pt={1}>
                             <Typography className={classes.actor}>
                                {'To:'}
                            </Typography>
                            <Stack direction="column" alignItems="flex-start" justifyContent="center">
                                <Typography className={classes.label}>
                                    {source?.name}
                                </Typography>
                                <Typography className={classNames(classes.label, classes.sublabel)}>
                                    {source?.email}
                                </Typography>
                            </Stack>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" pt={1}>
                             <Typography className={classes.actor}>
                                {'For:'}
                            </Typography>
                            <Typography className={classes.label}>
                                {dashboard?.title}
                            </Typography>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button className={classes.cancelButton}
                        onClick={onClose}> {'Cancel'} </Button>
                    <LoadingButton variant="contained"
                        className={classes.saveButton}
                        loading={loading}
                        onClick={handleSend}
                    > {'Send'} </LoadingButton>
                </DialogActions>
            </>)}
        </Dialog>
    </>);
}

export default IntroductionRequestModal;
